import React, { useState, useCallback, useContext, useRef, useMemo, useEffect, UIEventHandler } from "react";
import styles from "./style.module.scss";
import ChatItem from "./featues/ChatItem";
import Dialog from "../Dialog";
import { Dialog as DialogT,
  ChatSocketReceiveMessageType,
  CurrentChatPage,
  MessageType,
  DialogLastMessage,
  ChatSocketReceiveUserMessageType,
  ChatSocketReceiveSystemMessageType,
  DialogType } from "../types";
import { Spinner, Text } from "@evvve/ui-kit";
import { ChatSocketIoContext } from "src/shared-features/Wrappers";
import {
  useGetDialogsMutation,
  useDeleteDialogMutation,
} from "src/store/chat/api";
import { useEventSpaceDialog } from "../hooks/useEventSpaceDialog";
import { useReceiveNewDialog } from "../hooks/useReceiveNewDialog";
import { createDialogfromApi } from "../helpers";
import { useUpdatedDialog } from "../hooks/useUpdatedDialog";
import useUserDialogToOpen from "../hooks/useUserDialogToOpen";
import { useAppDispatch } from "src/hooks/useStoreHooks";
import { addToast } from "src/store/toast/slice";
import { DEFAULT_NOTIFICATION_ESTIMATE } from "src/constants";

type Props = {
  deviceType: "mobile" | "desktop";
  onClose?: () => void;
  onClickHeader?: () => void;
  isOpen: boolean;
}

const DialogList = (props: Props) => {
  //states
  const [currentChatPage, setCurrentChatPage] = useState<CurrentChatPage>(CurrentChatPage.DialogList);

  const [dialogList, setDialogList] = useState<DialogT[] | null>(null);
  const [isDialogsLoading, setIsDialogsLoading] = useState(true);

  const [nextDialogsToken, setNextDialogsToken] = useState<unknown | null>(null);
  const [isDialogsExhausted, setIsDialogsExhausted] = useState<boolean>(false);

  const [currentDialogId, setCurrentDialogId] = useState<string | null>(null);
  const [currentDialog, setCurrentDialog] = useState<DialogT | null>(null);

  const [isOpenUserDialogToOpen, setIsOpenUserDialogToOpen] = useState(false);

  const dispatch = useAppDispatch();

  //getDialogs [concern]
  const chatSocket = useContext(ChatSocketIoContext);
  const [getDialogs, getDialogsState] = useGetDialogsMutation();
  useEffect(() => {
    if (!chatSocket || chatSocket.connecting)
      return;

    if (currentChatPage === CurrentChatPage.DialogList && props.isOpen) {
      setDialogList(null);
      getDialogs();
    }

  }, [chatSocket?.connecting, currentChatPage, props.isOpen]);

  //get event space dialog [concern]
  const eventSpaceDialogState = useEventSpaceDialog();

  //concern user dialog to open [concern]
  const userDialogToOpen = useUserDialogToOpen();

  useEffect(() => {
    if (userDialogToOpen.isUninitialized)
      return;

    if (userDialogToOpen.isLoading || userDialogToOpen.isSuccess) {
      setIsOpenUserDialogToOpen(true);
      setCurrentChatPage(CurrentChatPage.Dialog);
    }

    if (userDialogToOpen.isError) {
      setIsOpenUserDialogToOpen(false);
      setCurrentChatPage(CurrentChatPage.DialogList);
      dispatch(addToast({
        type: "default",
        content: "Something went wrong. Please try later",
        estimate: DEFAULT_NOTIFICATION_ESTIMATE,
      }));
    }

  }, [userDialogToOpen]);

  //delete dialog [concern][reflex]
  useEffect(() => {
    if (!chatSocket?.chatSocket)
      return;

    const deleteHandler = ({ chat_id }: { chat_id: string }) => {
      setDialogList(pdl => pdl?.filter(d => d.id !== chat_id) || null);
      setCurrentDialogId(pcdid => pcdid === chat_id ? null : pcdid);
      setCurrentDialog(pcd => pcd?.id === chat_id ? null : pcd);
    };

    chatSocket?.chatSocket.on("deleteDialog", deleteHandler);
    return () => {
      chatSocket?.chatSocket?.off("deleteDialog", deleteHandler);
    };

  }, [chatSocket?.chatSocket]);

  //update dialog [concern]
  const updatedDialog = useUpdatedDialog();

  //new dailog [concern]
  const newDialog = useReceiveNewDialog();

  //receiveMessage [concern][reflex]
  useEffect(() => {
    if (chatSocket?.chatSocket && dialogList &&
      currentChatPage === CurrentChatPage.DialogList) {
      const onReceiveMessageHandler = (msg: ChatSocketReceiveMessageType) => {
        const eventDialog = dialogList.find(d => d.id === msg.message.chat_id);
        if (!eventDialog)
          return;

        let lastMessage: DialogLastMessage | null = null;
        if ((msg as ChatSocketReceiveUserMessageType).user)
          lastMessage = {
            type: MessageType.User,
            text: (msg as ChatSocketReceiveUserMessageType).message.text,
            username: (msg as ChatSocketReceiveUserMessageType).user.data.name,
            datetime: (msg as ChatSocketReceiveUserMessageType).message.datetime,
          };
        else if ((msg as ChatSocketReceiveSystemMessageType).message.type === MessageType.System)
          lastMessage = {
            type: MessageType.System,
            systemMessageType: (msg as ChatSocketReceiveSystemMessageType).message.system_metadata.type,
            datetime: (msg as ChatSocketReceiveSystemMessageType).message.datetime,
          };

        eventDialog.lastMessage = lastMessage;
        eventDialog.isWarning = (msg as ChatSocketReceiveSystemMessageType).message.type === MessageType.System;

        if (!eventDialog.unreadMessagesCount)
          eventDialog.unreadMessagesCount = 0;
        eventDialog.unreadMessagesCount++;

        setDialogList(prev => prev && prev.map(d => {
          if (d.id === msg.message.chat_id)
            return eventDialog;
          return d;
        }));
      };

      chatSocket?.chatSocket?.on("receiveMessage", onReceiveMessageHandler);
      return () => {
        chatSocket?.chatSocket?.off("receiveMessage", onReceiveMessageHandler);
      };
    }
  }, [currentChatPage, dialogList]);

  //load next dialogs [concern]
  const loadNextDialogs = useCallback(
    () => nextDialogsToken && getDialogs({ lastEvaluatedKey: nextDialogsToken }),
    [nextDialogsToken]
  );

  const scrollHandler: UIEventHandler | undefined = useMemo(() => {
    if (isDialogsExhausted)
      return;

    return ((e) => {
      const ct = e.currentTarget;
      if (Math.abs(ct.scrollHeight - ct.scrollTop - ct.clientHeight) <= 1) {
        prevLastDialogId.current = dialogList?.length ? dialogList.at(-1)?.id : null;
        loadNextDialogs();
      }
    });
  }, [isDialogsExhausted, loadNextDialogs]);

  //quit dialog [concern]
  //quit dialog is loading [reflex][state]
  const [deleteDialog, deleteDialogState] = useDeleteDialogMutation();
  const quitDialog = useCallback((chatId: string) => {
    chatId && deleteDialog({ chatId }).
      then(() => {
        setDialogList(null);
        if (currentChatPage !== CurrentChatPage.DialogList)
          setCurrentChatPage(CurrentChatPage.DialogList);
        else
          getDialogs();
      });
  }, []);

  //choose dialog [concern][reflex]
  const chooseDialog = useCallback((dialogId: string) => {
    dialogId && setCurrentDialogId(dialogId);
    setCurrentChatPage(CurrentChatPage.Dialog);
  }, []);

  //dialog list is open [reflex]
  useEffect(() => {
    !props.isOpen && setCurrentChatPage(CurrentChatPage.DialogList);
  }, [props.isOpen]);

  //dialogList [reflex]
  useEffect(() => {
    if (!getDialogsState.data)
      return;

    const dialogs = getDialogsState.data.dialogs.map(createDialogfromApi);

    setDialogList(prev => {
      if (!prev)
        return dialogs;

      return [...prev, ...dialogs];
    });

  }, [getDialogsState]);

  //update dialog [reflex]
  useEffect(() => {
    updatedDialog && setDialogList(dl => dl && dl.map(d => {
      if (d.id !== updatedDialog.id)
        return d;

      return {
        ...d,
        title: updatedDialog.title,
        avatarUrl: updatedDialog.avatarUrl,
      };
    }));
  }, [updatedDialog]);

  //isDialogsLoading [reflex]
  useEffect(() => {
    setIsDialogsLoading(chatSocket?.connecting === undefined ||
      chatSocket.connecting === true ||
      getDialogsState.isLoading);
  }, [chatSocket?.connecting, getDialogsState.isLoading]);

  //current dialog [reflex]
  //event space case
  useEffect(() => {
    //this means that it can transfer to event space dialog only from dialogList chat page
    if (currentChatPage !== CurrentChatPage.DialogList)
      return;

    const esd = eventSpaceDialogState.dialog;
    if (!esd)
      return;

    setCurrentDialogId(esd.id); //todo: think about currentDialogId and currentDialog states and their senses
    setCurrentDialog(esd);
    setCurrentChatPage(CurrentChatPage.Dialog);

  },
  [eventSpaceDialogState.dialog]);

  //others
  useEffect(() => {
    const d = currentDialogId && dialogList?.find(d => d.id === currentDialogId) || null;
    //todo: to think make it better
    //actually this is temporal solution to make it work with eventspace dialog
    setCurrentDialog(pd => pd?.id === currentDialogId ? pd : d);

  },
  [currentDialogId, dialogList]);

  //new dialog [reflex]
  useEffect(() => {
    newDialog && setDialogList(dl => dl ? [newDialog, ...dl] : [newDialog]);
  }, [newDialog]);

  //next dialogs token, is dialog list exshausted [reflex]
  useEffect(() => {
    if (!getDialogsState.data)
      return;

    const t = getDialogsState.data.last_evaluated_key;
    if (t)
      setNextDialogsToken(t);
    else {
      setNextDialogsToken(null);
      setIsDialogsExhausted(true);
    }
  }, [getDialogsState.data]);

  const prevLastDialogId = useRef<string | null | undefined>(null);

  const dialogs = useMemo(() => (
    dialogList?.map(d => (
      <div
        key={d.id}
        className={styles.item_style}
        ref={(node) => {
          //if loaded new dialog then it scroll on previos last one
          (d.id === prevLastDialogId.current) &&
            requestAnimationFrame(() => {
              node?.scrollIntoView({ block: "center" });
              prevLastDialogId.current = null;
            });
        }}
      >
        <ChatItem
          dialog={d}
          quitChatHandler={() => d.id && quitDialog(d.id)}
          chooseChatHandler={() => d.id && chooseDialog(d.id)}
          isQuitChatLoading={deleteDialogState.isLoading}
        />
      </div>
    ))
  ), [dialogList, deleteDialogState.isLoading]);

  if (isOpenUserDialogToOpen && currentChatPage === CurrentChatPage.Dialog)
    if (userDialogToOpen.isLoading || isDialogsLoading)
      return (
        <div className={styles.spinner}>
          <Spinner size="l" color="grayscale400" />
        </div>
      );
    else if (userDialogToOpen.isSuccess)
      return <Dialog
        dialog={userDialogToOpen.dialog || void 0}
        dialogType={DialogType.Personal}
        dialogMate={userDialogToOpen.user}
        onBack={() => {
          setIsOpenUserDialogToOpen(false);
          setCurrentChatPage(CurrentChatPage.DialogList);
        }}
      />;

  if (currentChatPage === CurrentChatPage.Dialog)
    if (currentDialog)
      return <Dialog
        dialog={currentDialog}
        dialogType={currentDialog.type}
        onBack={() => {
          setCurrentChatPage(CurrentChatPage.DialogList);
          setCurrentDialogId(null);
        }}
      />;
    else
      return (
        <div className={styles.spinner}>
          <Spinner size="l" color="grayscale400" />
        </div>
      );


  return(
    <>
      <div
        className={[styles.header, styles.header_mobile].join(" ")}
      >
        <Text size={["m", "m", "m", "m", 6]} bold>
          Chat
        </Text>
      </div>
      {props.isOpen && (
        <>
          {/* <div className={search}>
            <div className={search_input}>
              <Input
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.currentTarget.value);
                  // filterData(e.currentTarget.value, dialogList);
                }}
                startAdornment={<Search color="grayscale600" size="m" />}
                placeholder="Search by name"
                size="m"
              />
            </div>
            <div className={bell}>
              <MuteBellButton
                isMute={isMute}
                onClick={() => setIsMute(!isMute)}
              />
            </div>
          </div> */}
          {isDialogsLoading || eventSpaceDialogState.isLoading ?
            (
              <div className={styles.spinner}>
                <Spinner size="l" color="grayscale400" />
              </div>
            ) :
            <div className={styles.chatList}
              onScroll={scrollHandler}
            >
              <div className={styles.wrap}>
                {dialogs}
              </div>
              {/* {(props.searchValue && props.chatsList.length === 0) && (
                <div className={styles.no_matches_texts}>
                  <Icons.XCircle size="m" color="grayscale600" />
                  <div className={styles.no_matches_text}>
                    <Text
                      size="m"
                      color="grayscale600"
                    >
                                                No matches for
                    </Text>
                    <Text
                      size="m"
                      color="grayscale600"
                    >
                      {`“${props.searchValue}” found`}
                    </Text>
                  </div>
                </div>
              )} */}
            </div>}
        </>
      )}
    </>);
};
export default DialogList;